import { Handle, Position } from 'reactflow';
import { AddCircle, MoreVert } from '@mui/icons-material';
import WorkflowBoxHeader from '../WorkflowBox/WorkflowBoxHeader';
import { WorkflowServiceIcon } from '../Common/WorkflowServiceIcon';
import WorkflowBoxMenu from '../WorkflowBox/WorkflowBoxMenu';
import { NodeTestRunInstance, WorkflowNode, WorkflowNode as WorkflowNodeType } from '../../types/workflow';
import { useEffect, useState, useRef } from 'react';
import { useGetConfigsForNode, useGetNodeTestRunInstance, useUpdateNodeName } from '../../api/nodeApi';
import { NodeConfig } from '../../types/nodes';

interface WorkflowNodeProps {
    id: string;
    data: {
        workflowNode: WorkflowNodeType;
        onNodeClick: (node: WorkflowNodeType) => void;
        isLeaf: boolean;
        workflowType: string;
        workflowBoxIndex: number;
        onHeaderClick: () => void;
        onMenuClick: () => void;
        [key: string]: any;
        updateNodeConfigsFromRunInstances: (nodeId: string, response_config: string, nodeConfigs?: Record<string, NodeConfig>) => void;
        setTestRunInstances: React.Dispatch<React.SetStateAction<Record<string, NodeTestRunInstance>>>;
        deleteNodeById: (params: { nodeId: string }) => void;
        shouldDeleteNode: () => void;
        setNodeConfigs: React.Dispatch<React.SetStateAction<Record<string, NodeConfig>>>;
        setNodeResponseConfigs: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
        setNodeConfigsForNodeInRef: (nodeId: string, newNodeConfigs: NodeConfig) => void;
        refreshWorkflowNodes: () => void;
        getHasChildren: (node: WorkflowNode) => boolean;
    };
}

const FlowWorkflowNode = ({ id, data }: WorkflowNodeProps) => {
    const { data: testRunInstance, refetch: refetchTestRunInstance } = useGetNodeTestRunInstance(data.workflowNode.node?.id);
    const { mutate: fetchSavedNodeConfig } = useGetConfigsForNode();
    const { mutate: updateNodeName } = useUpdateNodeName();
    const [menuAnchorEl, setMenuAnchorEl] = useState<SVGSVGElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(data.workflowNode.node?.name || '');
    const isRenamingFromMenu = useRef(false);

    useEffect(() => {
        const outputData = testRunInstance?.nodeTestRunInstance?.flattened_output;
        if (outputData) data.updateNodeConfigsFromRunInstances(data.workflowNode.node?.id || "", outputData);
        if (testRunInstance?.nodeTestRunInstance) {
            data.setTestRunInstances(prev => ({
                ...prev,
                [data.workflowNode.node?.id || ""]: testRunInstance.nodeTestRunInstance
            }));
        }
    }, [testRunInstance, data.updateNodeConfigsFromRunInstances, data.workflowNode.node?.id]);

    useEffect(() => {
        const nodeId = data.workflowNode.node?.id;
        if (nodeId) {
            refreshSavedRunConfig();
        }
    }, [fetchSavedNodeConfig, data.workflowNode.node?.id]);

    const refreshSavedRunConfig = () => {
        fetchSavedNodeConfig({ nodeId: data.workflowNode.node?.id || "" }, {
            onSuccess: (res) => {
                data.setNodeConfigs(
                    (prev: Record<string, NodeConfig>) => ({
                        ...prev,
                        [data.workflowNode.node?.id || ""]: res
                    })
                );
                data.setNodeConfigsForNodeInRef(data.workflowNode.node?.id || "", res);
            }
        });
    }

    const handleTitleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editedTitle.trim() && data.workflowNode.node?.id) {
            updateNodeName(
                { nodeId: data.workflowNode.node.id, name: editedTitle },
                {
                    onSuccess: () => {
                        setIsEditing(false);
                        data.refreshWorkflowNodes();
                    }
                }
            );
        }
    };

    const startRename = () => {
        isRenamingFromMenu.current = true;
        setIsEditing(true);
        setEditedTitle(data.workflowNode.node?.name || '');
        // Reset the flag after a short delay
        setTimeout(() => {
            isRenamingFromMenu.current = false;
        }, 100);
    };


    return (
        <div 
            className="w-[180px] h-[120px] cursor-pointer bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-200 flex flex-col items-center justify-center p-3" 
            onClick={() => data.onNodeClick(data.workflowNode)}
        >
            <Handle
                type="target"
                position={Position.Left}
                id={`${id}-left`}
                style={{
                    left: -6,
                    background: '#6366f1',
                    width: '10px',
                    height: '10px',
                    border: '2px solid white',
                }}
            />
            {/* Compact header with prominent logo */}
            <div className="flex flex-col items-center justify-center h-full w-full">
                <div className="mb-2">
                    <WorkflowServiceIcon
                        workflowType={data.workflowNode.node?.service || 'default'}
                        alt={data.workflowNode.node?.service || 'default'}
                        height={32}
                        width={32}
                    />
                </div>
                <div className="text-center">
                    {isEditing ? (
                        <form onSubmit={handleTitleSubmit} onClick={(e) => e.stopPropagation()}>
                            <input
                                type="text"
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                className="text-xs font-medium text-gray-700 bg-transparent border border-gray-300 rounded px-1 py-0.5 w-full max-w-[120px] text-center"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setIsEditing(false);
                                    }
                                }}
                                onBlur={(e) => {
                                    if (!isRenamingFromMenu.current) {
                                        handleTitleSubmit(e as unknown as React.FormEvent);
                                    }
                                }}
                            />
                        </form>
                    ) : (
                        <div 
                            className="text-xs font-medium text-gray-700 truncate max-w-[140px] cursor-pointer"
                            onDoubleClick={startRename}
                        >
                            {data.workflowNode.node?.name || data.workflowNode.node?.service || 'Node'}
                        </div>
                    )}
                    {data.workflowNode.node?.action && (
                        <div className="text-xs text-gray-500 truncate max-w-[140px] mt-1">
                            {data.workflowNode.node.action}
                        </div>
                    )}
                </div>
                {/* Node number in purple circle */}
                <div className="absolute top-2 left-2">
                    <div className="w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                            {data.workflowBoxIndex}
                        </span>
                    </div>
                </div>

                {/* Status indicator and menu */}
                <div className="absolute top-2 right-2 flex items-center gap-1">
                    {data.workflowNode.node?.id ? (
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    ) : (
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    )}
                    <MoreVert 
                        className="text-gray-400 hover:text-gray-600 cursor-pointer" 
                        fontSize="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchorEl(e.currentTarget as SVGSVGElement);
                        }}
                    />
                </div>
            </div>
            {data.isLeaf && (
                <>
                    <svg
                        style={{
                            position: 'absolute',
                            right: -80,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '72px',
                            height: '2px',
                            overflow: 'visible'
                        }}
                    >
                        <path
                            style={{ 
                                stroke: '#B1B1B1', 
                                strokeWidth: 2,
                                strokeDasharray: '5',
                            }}
                            d={`M 0 0 L 64 0`}
                        />
                        <circle
                            cx={64}
                            cy={0}
                            r={12}
                            fill="white"
                            stroke="#B1B1B1"
                            strokeWidth={1}
                        />
                    </svg>
                    <div style={{ 
                        position: 'absolute', 
                        right: -84,
                        top: '50%',
                        transform: 'translate(0px, -50%)',
                        width: 24,
                        height: 24
                    }}>
                        <AddCircle 
                            fontSize="small"
                            className="cursor-pointer text-blue-500 hover:text-blue-600"
                            sx={{ width: 24, height: 24 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                data.handleAddNodeClick(data.workflowNode)
                            }}
                        />
                    </div>
                </>
            )}
            
            <Handle
                type="source"
                position={Position.Right}
                id={`${id}-right`}
                style={{ 
                    right: -6,
                    background: '#6366f1',
                    width: '10px',
                    height: '10px',
                    border: '2px solid white',
                }}
            />
            
            {/* Menu */}
            <WorkflowBoxMenu
                menuAnchorEl={menuAnchorEl}
                setMenuAnchorEl={setMenuAnchorEl}
                workflowNode={data.workflowNode}
                deleteNodeById={data.deleteNodeById}
                shouldDeleteNode={data.shouldDeleteNode}
                refreshWorkflowNodes={data.refreshWorkflowNodes}
                hasChildren={data.getHasChildren(data.workflowNode)}
                onStartRename={startRename}
            />
        </div>
    );
};

export default FlowWorkflowNode; 